import i18n from '../i18n';

export class PureFrontendVideoPlayer {
  private sceneList: any[];
  private currentIndex: number = 0;
  private container: HTMLElement;
  private mediaContainer: HTMLElement; // 统一的媒体容器
  private videoElement: HTMLVideoElement;
  private imageElement: HTMLImageElement;
  private placeholderElement: HTMLDivElement;
  private subtitleElement: HTMLElement;
  private audioElement: HTMLAudioElement | null = null;
  private controlsContainer: HTMLElement;
  private playPauseButton: HTMLButtonElement;
  private progressBar: HTMLInputElement;
  private currentTimeDisplay: HTMLSpanElement;
  private totalTimeDisplay: HTMLSpanElement;
  public isPlaying: boolean = false;
  private hasEnded: boolean = false; // 播放是否已结束
  private currentSubtitle: string = ''; // 当前场景的字幕内容
  private imageTimeoutId: number | null = null;
  private progressInterval: number | null = null;
  private audioSubtitleTimeoutId: number | null = null;
  private isInitialized: boolean = false; // 防止重复初始化
  // private playbackStartTime: number = 0; // 播放开始时间 - 暂时注释，未使用
  private totalDuration: number = 0; // 所有场景总时长
  private pausedSceneIndex: number = -1; // 暂停时的场景索引

  // 背景音乐相关属性
  private bgMusicElement: HTMLAudioElement | null = null;
  private bgMusicUrl: string | null = null;
  private bgMusicVolume: number = 0.5;

  // 字幕样式相关属性（新增）
  private subtitleStyle: any = null;  // 字幕样式配置

  // 视频分辨率属性（新增）
  private videoWidth: number = 1920;   // 实际视频宽度，默认1920
  private videoHeight: number = 1080;  // 实际视频高度，默认1080

  constructor(container: HTMLElement, sceneList: any[], skipAutoLoad: boolean = false, bgMusicUrl: string | null = null, bgMusicVolume: number = 0.5, videoWidth?: number, videoHeight?: number, subtitleStyle?: any) {
    console.log('🎬 初始化播放器', { container, sceneListLength: sceneList.length, skipAutoLoad, bgMusicUrl, videoWidth, videoHeight, subtitleStyle });
    this.container = container;
    this.sceneList = sceneList;

    // 存储字幕样式（如果提供）
    if (subtitleStyle) {
      this.subtitleStyle = subtitleStyle;
      console.log('🎬 设置字幕样式:', subtitleStyle);
    }

    // 存储视频分辨率（如果提供）
    if (videoWidth) {
      this.videoWidth = videoWidth;
    }
    if (videoHeight) {
      this.videoHeight = videoHeight;
    }

    // 初始化背景音乐属性
    this.bgMusicUrl = bgMusicUrl;
    this.bgMusicVolume = bgMusicVolume;

    // 如果有背景音乐URL，创建背景音乐元素
    if (this.bgMusicUrl) {
      this.bgMusicElement = new Audio();
      this.bgMusicElement.src = this.bgMusicUrl;
      this.bgMusicElement.volume = this.bgMusicVolume;
      this.bgMusicElement.loop = true; // 背景音乐循环播放
      this.bgMusicElement.preload = 'auto';
    }

    // 计算所有场景的总时长
    this.calculateTotalDuration();

    // 创建统一的媒体容器
    this.mediaContainer = document.createElement('div');
    this.mediaContainer.className = 'w-full h-full bg-gray-900 flex items-center justify-center relative';
    this.mediaContainer.style.position = 'relative';

    // 初始化视频元素 - 初始隐藏
    this.videoElement = document.createElement('video');
    this.videoElement.className = 'absolute inset-0 w-full h-full object-contain';
    this.videoElement.style.display = 'none';
    this.videoElement.controls = false;
    this.videoElement.removeAttribute('src');

    // 初始化图片元素 - 初始隐藏
    this.imageElement = document.createElement('img');
    this.imageElement.className = 'max-w-full max-h-full object-contain';
    this.imageElement.style.display = 'none';
    this.imageElement.style.position = 'absolute';
    this.imageElement.style.top = '50%';
    this.imageElement.style.left = '50%';
    this.imageElement.style.transform = 'translate(-50%, -50%)';

    // 添加占位文字元素
    this.placeholderElement = document.createElement('div');
    this.placeholderElement.className = 'text-gray-400 text-center';
    this.placeholderElement.style.position = 'absolute';
    this.placeholderElement.style.top = '50%';
    this.placeholderElement.style.left = '50%';
    this.placeholderElement.style.transform = 'translate(-50%, -50%)';
    this.placeholderElement.style.fontSize = '14px';
    this.placeholderElement.style.display = 'none'; // 默认隐藏

    // 创建控制条容器
    this.controlsContainer = document.createElement('div');
    this.controlsContainer.className = 'absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4';
    this.controlsContainer.style.zIndex = '10'; // 确保控制条在字幕上方

    // 创建控制条内容
    const controlsContent = document.createElement('div');
    controlsContent.className = 'flex items-center gap-3';

    // 播放/暂停按钮
    this.playPauseButton = document.createElement('button');
    this.playPauseButton.className = 'w-10 h-10 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center text-white transition-colors';
    this.playPauseButton.innerHTML = '▶';
    this.playPauseButton.addEventListener('click', () => this.togglePlayPause());

    // 时间显示
    this.currentTimeDisplay = document.createElement('span');
    this.currentTimeDisplay.className = 'text-white text-sm min-w-[40px]';
    this.currentTimeDisplay.textContent = '0:00';

    const separator = document.createElement('span');
    separator.className = 'text-white text-sm';
    separator.textContent = '/';

    this.totalTimeDisplay = document.createElement('span');
    this.totalTimeDisplay.className = 'text-white text-sm min-w-[40px]';
    this.totalTimeDisplay.textContent = '0:00';

    // 进度条
    this.progressBar = document.createElement('input');
    this.progressBar.type = 'range';
    this.progressBar.className = 'flex-1 h-1 bg-white/30 rounded-lg appearance-none cursor-pointer slider';
    this.progressBar.min = '0';
    this.progressBar.max = '100';
    this.progressBar.value = '0';
    this.progressBar.addEventListener('click', (event) => this.handleProgressBarClick(event));
    this.progressBar.addEventListener('input', () => this.handleProgressChange());
    this.progressBar.addEventListener('change', () => this.handleProgressChange());

    // 组装控制条
    controlsContent.appendChild(this.playPauseButton);
    controlsContent.appendChild(this.currentTimeDisplay);
    controlsContent.appendChild(separator);
    controlsContent.appendChild(this.totalTimeDisplay);
    controlsContent.appendChild(this.progressBar);
    this.controlsContainer.appendChild(controlsContent);

    // 初始化字幕元素
    this.subtitleElement = document.createElement('div');
    // ✅ 只保留布局相关的类,移除所有样式相关的类(text-white, text-center, font-normal等)
    // 样式完全由 applySubtitleStyle() 动态设置
    this.subtitleElement.className = 'absolute bottom-12 left-1/2 w-auto max-w-[90%]';
    this.subtitleElement.style.transform = 'translateX(-50%)';
    // ✅ 移除了 text-base 类，让 JavaScript 动态设置 fontSize（与后端保持一致）
    this.subtitleElement.style.zIndex = '5'; // 修改为5，确保在进度条下方（进度条等控制条默认层级更高）
    // ✅ 移除默认的 textShadow,改为由 applySubtitleStyle() 根据 outlineWidth 和 outlineColor 动态设置
    this.subtitleElement.style.pointerEvents = 'none'; // 确保不会阻挡点击事件
    this.subtitleElement.style.whiteSpace = 'normal'; // 允许换行
    this.subtitleElement.style.overflow = 'visible'; // 确保内容可见
    this.subtitleElement.style.display = 'none'; // 默认隐藏字幕
    this.subtitleElement.style.position = 'absolute'; // 相对于父容器绝对定位
    this.subtitleElement.style.margin = '0'; // 清除margin
    this.subtitleElement.style.boxSizing = 'border-box'; // 确保padding不会影响总宽度

    console.log('🟢 [SUBTITLE_DEBUG] 字幕元素初始化完成');
    console.log('🟢 [SUBTITLE_DEBUG] 初始className:', this.subtitleElement.className);
    console.log('🟢 [SUBTITLE_DEBUG] 初始display:', this.subtitleElement.style.display);

    // 初始化时应用字幕样式（如果有）（新增）
    if (this.subtitleStyle) {
      console.log('🟢 [SUBTITLE_DEBUG] 构造函数中有subtitleStyle,准备应用');
      this.applySubtitleStyle();
      console.log('🟢 [SUBTITLE_DEBUG] 构造函数中应用完成');
    } else {
      console.log('🟢 [SUBTITLE_DEBUG] 构造函数中没有subtitleStyle');
    }

    // 组装最终容器
    this.mediaContainer.appendChild(this.videoElement);
    this.mediaContainer.appendChild(this.imageElement);
    this.mediaContainer.appendChild(this.placeholderElement); // 占位符
    this.mediaContainer.appendChild(this.subtitleElement); // 字幕放在控制条之前
    this.mediaContainer.appendChild(this.controlsContainer);
    this.container.appendChild(this.mediaContainer);

    // 绑定事件
    this.videoElement.addEventListener('timeupdate', () => this.handleTimeUpdate());
    this.videoElement.addEventListener('ended', () => this.handleEnded());
    this.videoElement.addEventListener('loadedmetadata', () => this.handleLoadedMetadata());
    this.videoElement.addEventListener('play', () => {
      this.updatePlayPauseButton(true);
      this.isPlaying = true;
    });
    this.videoElement.addEventListener('pause', () => {
      // 只在非手动暂停时更新按钮状态（避免重复调用）
      if (!this.isPlaying) return;
      this.updatePlayPauseButton(false);
      this.isPlaying = false;
      // 暂停背景音乐
      if (this.bgMusicElement && !this.bgMusicElement.paused) {
        this.bgMusicElement.pause();
      }
    });

    // 添加样式
    this.addCustomStyles();

    // 如果有场景数据且不需要跳过自动加载，立即加载第一个场景
    if (this.sceneList.length > 0 && !this.isInitialized && !skipAutoLoad) {
      console.log('🎬 立即加载第一个场景');
      this.isInitialized = true;

      // 延迟一点时间确保DOM完全渲染后再加载场景
      setTimeout(() => {
        console.log('🎬 开始初始化加载第一个场景');
        this.loadScene(0);

        // 调试初始状态
        setTimeout(() => {
          //           console.log('🎬 [VideoPlayer] 初始化完成状态检查', {
          //             videoDisplay: this.videoElement.style.display,
          //             imageDisplay: this.imageElement.style.display,
          //             videoSrc: this.videoElement.src,
          //             imageSrc: this.imageElement.src,
          //             videoElement: this.videoElement,
          //             imageElement: this.imageElement
          //           });
        }, 500);
      }, 100);
    }
  }

  /**
   * 计算所有场景的总时长
   */
  private calculateTotalDuration(): void {
    this.totalDuration = this.sceneList.reduce((total, scene) => {
      const { endTime = 0 } = scene.cursorPosition || {};
      //       console.log('🎬 [VideoPlayer] 场景时长', {
      //         sceneId: scene.id,
      //         endTime,
      //         currentTotal: total,
      //         newTotal: total + endTime
      //       });
      return total + endTime;
    }, 0);
    //     console.log('🎬 [VideoPlayer] 总时长计算完成', {
    //       totalDuration: this.totalDuration,
    //       sceneCount: this.sceneList.length,
    //       totalMinutes: Math.floor(this.totalDuration / 60),
    //       totalSeconds: Math.floor(this.totalDuration % 60)
    //     });
  }

  /**
   * 获取到当前场景为止的累计时间
   */
  private getElapsedTimeUpToCurrentScene(): number {
    let totalTime = 0;
    //     console.log('🎬 [VideoPlayer] 计算累计时间', {
    //       currentIndex: this.currentIndex,
    //       totalScenes: this.sceneList.length
    //     });

    for (let i = 0; i < this.currentIndex; i++) {
      const { endTime = 0 } = this.sceneList[i].cursorPosition || {};
      //       console.log('🎬 [VideoPlayer] 累计场景', {
      //         sceneIndex: i,
      //         sceneId: this.sceneList[i].id,
      //         endTime,
      //         runningTotal: totalTime
      //       });
      totalTime += endTime;
    }

    //     console.log('🎬 [VideoPlayer] 累计时间计算完成', {
    //       currentIndex: this.currentIndex,
    //       elapsedTime: totalTime
    //     });

    return totalTime;
  }

  /**
   * 同步背景音乐时间
   * @param targetTime 目标时间（秒）
   */
  private syncBgMusicTime(targetTime: number): void {
    if (!this.bgMusicElement) return;

    // 确保目标时间在背景音乐的有效范围内
    const bgMusicDuration = this.bgMusicElement.duration || 0;
    if (bgMusicDuration > 0) {
      // 计算相对于背景音乐时长的位置（考虑循环播放）
      const normalizedTime = targetTime % bgMusicDuration;

      // 只有当时间差超过阈值时才更新，避免频繁调整
      if (Math.abs(this.bgMusicElement.currentTime - normalizedTime) > 0.1) {
        this.bgMusicElement.currentTime = normalizedTime;
      }
    }
  }

  /**
   * 处理播放时间更新
   */
  private handleTimeUpdate(): void {
    const currentScene = this.sceneList[this.currentIndex];
    if (!currentScene) return;

    const { startTime = 0, endTime } = currentScene.cursorPosition || {};
    const sceneDuration = endTime - startTime;
    const currentTimeInScene = this.videoElement.currentTime - startTime;

    // 视频在endTime前播放完成，画面停止
    if (this.videoElement.ended && currentTimeInScene < sceneDuration) {
      //       console.log('🎬 [VideoPlayer] 视频在endTime前播放完成，停止画面');
      this.videoElement.pause();
      // 继续显示静态画面，但不影响字幕逻辑
      return;
    }

    // 强制在endTime时停止
    if (this.videoElement.currentTime >= endTime) {
      //       console.log('🎬 [VideoPlayer] 视频到达endTime，强制停止');
      this.videoElement.pause();
      this.stopCurrentMedia();
      this.nextScene();
      return;
    }

    // 计算累计播放时间
    const elapsedInScene = this.videoElement.currentTime - startTime;
    const elapsedTotal = this.getElapsedTimeUpToCurrentScene() + elapsedInScene;
    const progress = (elapsedTotal / this.totalDuration) * 100;

    // 同步背景音乐时间
    this.syncBgMusicTime(elapsedTotal);

    // 更新字幕显示 - 按照startTime开始，endTime强制结束的逻辑
    const videoDuration = this.videoElement.duration || 0;
    const audioDuration = this.audioElement ? this.audioElement.duration : 0;

    // 计算各种媒体的结束时间
    const videoEndTime = videoDuration > 0 ? Math.min(videoDuration, sceneDuration) : sceneDuration;
    const audioEndTime = audioDuration > 0 ? Math.min(audioDuration, sceneDuration) : sceneDuration;
    // 字幕在任何媒体结束时就结束，或到endTime时强制结束
    const subtitleEndTime = Math.min(videoEndTime, audioEndTime);

    if (elapsedInScene >= 0 && elapsedInScene <= subtitleEndTime && this.currentSubtitle) {
      if (this.subtitleElement.textContent !== this.currentSubtitle) {
        this.subtitleElement.textContent = this.currentSubtitle;
        this.subtitleElement.style.display = 'block'; // 显示字幕背景
        //         console.log('🎬 [VideoPlayer] 视频场景字幕开始显示', {
        //           elapsedInScene,
        //           videoCurrentTime: this.videoElement.currentTime,
        //           startTime,
        //           endTime,
        //           sceneDuration,
        //           videoDuration,
        //           audioDuration,
        //           videoEndTime,
        //           audioEndTime,
        //           subtitleEndTime,
        //           subtitle: this.currentSubtitle
        //         });
      }
    } else if (elapsedInScene > subtitleEndTime) {
      // 超过显示时间，强制清空字幕
      if (this.subtitleElement.textContent !== '') {
        this.subtitleElement.textContent = '';
        //         console.log('🎬 [VideoPlayer] 视频场景字幕强制结束', {
        //           elapsedInScene,
        //           startTime,
        //           endTime,
        //           sceneDuration,
        //           videoDuration,
        //           audioDuration,
        //           subtitleEndTime,
        //           reason: '到达媒体播放结束时间或场景endTime强制停止'
        //         });
      }
    }

    // 更新进度条
    this.progressBar.value = progress.toString();
    this.currentTimeDisplay.textContent = this.formatTime(elapsedTotal);
    this.totalTimeDisplay.textContent = this.formatTime(this.totalDuration);
  }

  /**
   * 处理播放结束
   */
  private handleEnded(): void {
    //     console.log('🎬 [VideoPlayer] 视频播放结束事件触发', {
    //       currentIndex: this.currentIndex,
    //       totalScenes: this.sceneList.length,
    //       isPlaying: this.isPlaying
    //     });

    this.stopCurrentMedia();

    // 检查是否是最后一个场景
    if (this.currentIndex >= this.sceneList.length - 1) {
      //       console.log('🎬 [VideoPlayer] 最后一个场景播放完成，结束播放');
      this.showEndMessage();
    } else {
      //       console.log('🎬 [VideoPlayer] 切换到下一个场景');
      // 直接尝试加载下一个场景，让loadScene处理结束逻辑
      this.loadScene(this.currentIndex + 1);
      this.play();
    }
  }

  /**
   * 延迟加载音频和字幕
   */
  private scheduleAudioAndSubtitle(scene: any): void {
    const { startTime = 0, endTime } = scene.cursorPosition || { startTime: 0, endTime: 5 };
    const delayMs = startTime * 1000; // 转换为毫秒

    //     console.log('🎬 [VideoPlayer] 设置延迟加载音频和字幕', {
    //       sceneId: scene.id,
    //       startTime,
    //       endTime,
    //       delayMs,
    //       willLoadAt: sceneStartTime + delayMs
    //     });

    // 设置延迟加载音频和字幕
    this.audioSubtitleTimeoutId = window.setTimeout(() => {
      //       console.log('🎬 [VideoPlayer] startTime到达，开始加载音频和字幕', {
      //         sceneId: scene.id,
      //         elapsed: (Date.now() - sceneStartTime) / 1000
      //       });

      // 播放音频
      if (scene.audioUrl && scene.audioUrl !== '') {
        //         console.log('🎬 [VideoPlayer] 开始播放音频', {
        //           audioUrl: scene.audioUrl
        //         });

        // 如果音频URL变化了，创建新的音频元素
        if (!this.audioElement || this.audioElement.src !== scene.audioUrl) {
          this.audioElement = new Audio(scene.audioUrl);
        }

        // 音频从0开始播放
        this.audioElement.currentTime = 0;

        // 监听音频播放完成事件
        const handleAudioEnded = () => {
          //           console.log('🎬 [VideoPlayer] 音频自然播放完成，清空字幕');
          if (this.subtitleElement.textContent === this.currentSubtitle) {
            this.subtitleElement.textContent = '';
            this.subtitleElement.style.display = 'none'; // 隐藏字幕背景
          }
          // 移除事件监听器
          this.audioElement?.removeEventListener('ended', handleAudioEnded);
        };

        this.audioElement.addEventListener('ended', handleAudioEnded);

        this.audioElement.play().then(() => {
          //           console.log('🎬 [VideoPlayer] 音频播放成功', {
          //             audioUrl: scene.audioUrl,
          //             startTime: this.audioElement.currentTime,
          //             duration: this.audioElement.duration
          //           });
        }).catch(() => {
          //           console.error('🎬 [VideoPlayer] 音频播放失败');
        });

        // 设置音频在endTime时强制停止（2.2逻辑）
        const audioDuration = this.audioElement.duration || 0;
        const playDuration = Math.min(audioDuration, endTime - startTime);

        if (playDuration > 0) {
          setTimeout(() => {
            if (this.audioElement && !this.audioElement.paused) {
              //               console.log('🎬 [VideoPlayer] 到达endTime，强制停止音频播放');
              this.audioElement.pause();
              this.audioElement.removeEventListener('ended', handleAudioEnded);
            }
          }, playDuration * 1000);
        }
      }

      // 显示字幕
      if (this.currentSubtitle) {
        this.subtitleElement.textContent = this.currentSubtitle;
        this.subtitleElement.style.display = 'block'; // 显示字幕背景
        //         console.log('🎬 [VideoPlayer] 字幕开始显示，将跟随音频播放完成', {
        //           subtitle: this.currentSubtitle,
        //           startTime,
        //           endTime
        //         });
        // 字幕现在由音频ended事件控制，不需要在这里设置endTime强制停止
      }
    }, delayMs);
  }

  /**
   * 更新背景音乐
   * @param newBgMusicUrl 新的背景音乐URL
   * @param newBgMusicVolume 新的背景音乐音量
   */
  public updateBgMusic(newBgMusicUrl: string | null, newBgMusicVolume: number = 0.5): void {
    console.log('🎬 [VideoPlayer] 更新背景音乐', { newBgMusicUrl, newBgMusicVolume });

    // 更新背景音乐属性
    this.bgMusicUrl = newBgMusicUrl;
    this.bgMusicVolume = newBgMusicVolume;

    // 如果当前有背景音乐元素，先清理
    if (this.bgMusicElement) {
      this.bgMusicElement.pause();
      this.bgMusicElement.src = '';
      this.bgMusicElement = null;
    }

    // 如果有新的背景音乐URL，创建新的背景音乐元素
    if (this.bgMusicUrl) {
      this.bgMusicElement = new Audio();
      this.bgMusicElement.src = this.bgMusicUrl;
      this.bgMusicElement.volume = this.bgMusicVolume;
      this.bgMusicElement.loop = true;
      this.bgMusicElement.preload = 'auto';

      // 如果当前正在播放，自动播放新的背景音乐
      if (this.isPlaying) {
        this.bgMusicElement.play().catch(err => {
          console.error('🎬 [VideoPlayer] 更新背景音乐后播放失败:', err);
        });
      }
    }
  }

  /**
   * 更新字幕样式（新增）
   * @param newSubtitleStyle 新的字幕样式配置
   */
  public updateSubtitleStyle(newSubtitleStyle: any): void {
    console.log('🟢 [SUBTITLE_DEBUG] updateSubtitleStyle 被调用');
    console.log('🟢 [SUBTITLE_DEBUG] 接收到的样式:', JSON.stringify(newSubtitleStyle, null, 2));
    console.log('🟢 [SUBTITLE_DEBUG] this.subtitleElement存在:', !!this.subtitleElement);
    console.log('🟢 [SUBTITLE_DEBUG] 当前className:', this.subtitleElement?.className);
    console.log('🟢 [SUBTITLE_DEBUG] 当前display:', this.subtitleElement?.style.display);
    console.log('🟢 [SUBTITLE_DEBUG] 当前color:', this.subtitleElement?.style.color);
    console.log('🟢 [SUBTITLE_DEBUG] 当前fontSize:', this.subtitleElement?.style.fontSize);

    this.subtitleStyle = newSubtitleStyle;
    console.log('🟢 [SUBTITLE_DEBUG] 准备调用 applySubtitleStyle');
    this.applySubtitleStyle();
    console.log('🟢 [SUBTITLE_DEBUG] applySubtitleStyle 调用完成');

    // 验证应用后的样式
    console.log('🟢 [SUBTITLE_DEBUG] 验证应用后的样式:');
    console.log('🟢 [SUBTITLE_DEBUG]   - color:', this.subtitleElement?.style.color);
    console.log('🟢 [SUBTITLE_DEBUG]   - fontSize:', this.subtitleElement?.style.fontSize);
    console.log('🟢 [SUBTITLE_DEBUG]   - fontWeight:', this.subtitleElement?.style.fontWeight);
  }

  /**
   * 应用字幕样式到字幕元素（新增）
   */

  /**
   * 获取当前场景媒体的实际宽度
   * ✅ 修复：优先使用当前播放的媒体（视频/图片）的实际分辨率
   *
   * 逻辑：
   * 1. 前端预览时：获取当前场景的媒体（图片/视频）实际宽度
   * 2. 后端合成视频：使用构造函数传入的 videoWidth
   */
  private getVideoWidth(): number {
    // 情况1：查看后端合成的视频（有传入 videoWidth）
    if (this.videoWidth !== 1920) {
      console.log('🎬 [VideoPlayer] 使用合成视频的 videoWidth:', this.videoWidth);
      return this.videoWidth;
    }

    // 情况2：前端预览 - 从当前显示的媒体元素获取实际宽度
    // 优先级：视频元素 > 图片元素 > 默认值
    if (this.videoElement && this.videoElement.style.display !== 'none' && this.videoElement.videoWidth > 0) {
      console.log('🎬 [VideoPlayer] 从当前视频获取实际宽度:', this.videoElement.videoWidth);
      return this.videoElement.videoWidth;
    }

    if (this.imageElement && this.imageElement.style.display !== 'none' && this.imageElement.naturalWidth > 0) {
      console.log('🎬 [VideoPlayer] 从当前图片获取实际宽度:', this.imageElement.naturalWidth);
      return this.imageElement.naturalWidth;
    }

    // 回退：使用默认值1920（ASS基准宽度）
    console.log('🎬 [VideoPlayer] 使用默认 videoWidth: 1920');
    return 1920;
  }

  /**
   * 应用字幕样式
   * ✅ 修复：字幕大小只跟播放器容器有关，跟视频分辨率无关
   */
  private applySubtitleStyle(): void {
    console.log('🟢 [SUBTITLE_DEBUG] applySubtitleStyle 开始执行');
    if (!this.subtitleStyle || !this.subtitleElement) {
      console.log('🟢 [SUBTITLE_DEBUG] 缺少必要元素,退出');
      console.log('🟢 [SUBTITLE_DEBUG]   - this.subtitleStyle:', !!this.subtitleStyle);
      console.log('🟢 [SUBTITLE_DEBUG]   - this.subtitleElement:', !!this.subtitleElement);
      return;
    }

    // 确保字幕在控制条下方
    this.subtitleElement.style.zIndex = '5';

    const style = this.subtitleStyle;
    console.log('🟢 [SUBTITLE_DEBUG] 样式配置:', {
      fontName: style.fontName,
      fontSize: style.fontSize,
      primaryColor: style.primaryColor,
      outlineColor: style.outlineColor,
      outlineWidth: style.outlineWidth,
      alignment: style.alignment,
      bold: style.bold,
      templateId: style.templateId
    });

    // ✅ 正确方案：字幕大小应该相对于播放器容器，而不是视频分辨率
    //
    // 核心原理：
    //   不管视频是1024px、1920px还是其他分辨率，只要播放器容器固定，
    //   字幕的绝对像素大小就应该是一样的，这样看起来大小才一致。
    //
    // 后端逻辑（ASS格式）：
    //   - PlayResX=1920（固定基准）
    //   - fontSize=36（相对1920）
    //   - FFmpeg会根据实际视频分辨率自动缩放
    //
    // 前端逻辑：
    //   - 使用同样的1920作为基准
    //   - 字幕大小 = fontSize * (containerWidth / 1920)
    //   - 只跟容器宽度有关，跟视频分辨率无关
    //
    // 示例：
    //   容器400px，视频1024px: 36 * (400/1920) = 7.5px
    //   容器400px，视频1920px: 36 * (400/1920) = 7.5px
    //   容器600px，视频1024px: 36 * (600/1920) = 11.25px
    //
    // 这样确保了：
    //   ✅ 不同分辨率视频，字幕大小一致（相同容器）
    //   ✅ 前后端相对大小一致
    //   ✅ 字幕大小只跟播放器容器有关

    // 🔍 详细调试：检查DOM结构和实际显示尺寸
    console.log('🎬 [调试] DOM结构分析:');
    console.log('  - container:', this.container.className, 'offsetWidth:', this.container.offsetWidth, 'offsetHeight:', this.container.offsetHeight);
    console.log('  - mediaContainer:', this.mediaContainer.className, 'offsetWidth:', this.mediaContainer.offsetWidth, 'offsetHeight:', this.mediaContainer.offsetHeight);
    console.log('  - imageElement:', this.imageElement.className, 'display:', this.imageElement.style.display);
    if (this.imageElement.style.display !== 'none') {
      console.log('    - naturalWidth:', this.imageElement.naturalWidth, 'naturalHeight:', this.imageElement.naturalHeight);
      console.log('    - width:', this.imageElement.width, 'height:', this.imageElement.height);
      // 🔍 关键：获取图片的实际显示尺寸
      const imageRect = this.imageElement.getBoundingClientRect();
      console.log('    - getBoundingClientRect:', imageRect.width, 'x', imageRect.height);
    }
    console.log('  - subtitleElement:', this.subtitleElement.className, 'offsetWidth:', this.subtitleElement.offsetWidth);

    const containerWidth = this.mediaContainer.offsetWidth;  // 使用实际显示媒体的容器
    const ASS_BASE_WIDTH = 1920;  // ASS格式固定基准宽度
    const scaleRatio = containerWidth / ASS_BASE_WIDTH;
    const scaledFontSize = style.fontSize * scaleRatio;

    console.log('🎬 [前端字幕计算] ===== 字幕计算详情 =====');
    console.log('🎬 [前端字幕计算] 容器信息:');
    console.log('  - this.container.offsetWidth:', this.container.offsetWidth, 'px (外层容器)');
    console.log('  - this.mediaContainer.offsetWidth:', this.mediaContainer.offsetWidth, 'px (媒体容器)');
    console.log('🎬 [前端字幕计算] ASS基准信息:');
    console.log('  - ASS_BASE_WIDTH:', ASS_BASE_WIDTH, 'px (固定)');
    console.log('🎬 [前端字幕计算] 字体样式:');
    console.log('  - fontName:', style.fontName);
    console.log('  - fontSize:', style.fontSize, '(相对ASS基准1920)');
    console.log('  - primaryColor:', style.primaryColor);
    console.log('  - outlineColor:', style.outlineColor);
    console.log('  - outlineWidth:', style.outlineWidth);
    console.log('  - alignment:', style.alignment);
    console.log('🎬 [前端字幕计算] 缩放计算:');
    console.log('  - scaleRatio:', containerWidth, '/', ASS_BASE_WIDTH, '=', scaleRatio.toFixed(4));
    console.log('  - scaledFontSize:', style.fontSize, '*', scaleRatio.toFixed(4), '=', scaledFontSize.toFixed(2) + 'px');
    console.log('🎬 [前端字幕计算] 最终结果:');
    console.log('  - 字幕将在', containerWidth, 'px宽的容器中显示为', scaledFontSize.toFixed(2), 'px');
    console.log('  - 相对大小:', (scaledFontSize / containerWidth * 100).toFixed(2) + '%');
    console.log('🎬 [前端字幕计算] =========================');


    // 应用字体样式（按比例缩放字体大小）
    console.log('🟢 [SUBTITLE_DEBUG] 开始应用样式到DOM元素');
    this.subtitleElement.style.fontFamily = `${style.fontName}, Arial, sans-serif`;
    this.subtitleElement.style.fontSize = `${scaledFontSize}px`;
    this.subtitleElement.style.fontWeight = style.bold ? 'bold' : 'normal';
    this.subtitleElement.style.fontStyle = style.italic ? 'italic' : 'normal';
    this.subtitleElement.style.color = style.primaryColor;
    // ✅ 设置line-height为1
    this.subtitleElement.style.setProperty('line-height', '1', 'important');
    // 🔥 使用transform scale(1.5)放大字幕，匹配后端ASS ScaleY=100的视觉效果
    const scaleTransform = 'translateX(-50%) scale(1.5)';
    this.subtitleElement.style.transform = scaleTransform;
    this.subtitleElement.style.transformOrigin = 'center bottom';

    console.log('🟢 [SUBTITLE_DEBUG] 已设置核心样式:');
    console.log('🟢 [SUBTITLE_DEBUG]   - fontFamily:', this.subtitleElement.style.fontFamily);
    console.log('🟢 [SUBTITLE_DEBUG]   - fontSize:', this.subtitleElement.style.fontSize);
    console.log('🟢 [SUBTITLE_DEBUG]   - fontWeight:', this.subtitleElement.style.fontWeight);
    console.log('🟢 [SUBTITLE_DEBUG]   - color:', this.subtitleElement.style.color);
    console.log('🟢 [SUBTITLE_DEBUG]   - transform:', this.subtitleElement.style.transform);

    // 🔍 调试：检查浏览器实际渲染的fontSize和元素尺寸
    setTimeout(() => {
      const computedStyle = window.getComputedStyle(this.subtitleElement);
      const actualFontSize = computedStyle.fontSize;
      const actualFontSizeValue = parseFloat(actualFontSize);
      const actualPaddingTop = parseFloat(computedStyle.paddingTop);
      const actualPaddingBottom = parseFloat(computedStyle.paddingBottom);
      const actualLineHeight = computedStyle.lineHeight;

      console.log('🎬 [调试] 浏览器实际渲染分析:');
      console.log('  - 计算的fontSize:', scaledFontSize.toFixed(2) + 'px');
      console.log('  - 实际的fontSize:', actualFontSize);
      console.log('  - 差值:', (actualFontSizeValue - scaledFontSize).toFixed(2) + 'px');
      console.log('  - 实际lineHeight:', actualLineHeight);
      console.log('  - 实际padding:', actualPaddingTop.toFixed(2), 'px (top) +', actualPaddingBottom.toFixed(2), 'px (bottom) =', (actualPaddingTop + actualPaddingBottom).toFixed(2), 'px总padding');

      // 🔍 获取文字的实际渲染尺寸
      const textMetrics = this.subtitleElement.getBoundingClientRect();
      console.log('🎬 [调试] 字幕元素实际尺寸:');
      console.log('  - offsetWidth:', this.subtitleElement.offsetWidth, 'px');
      console.log('  - clientWidth:', this.subtitleElement.clientWidth, 'px');
      console.log('  - scrollWidth:', this.subtitleElement.scrollWidth, 'px');
      console.log('  - getBoundingClientRect:', textMetrics.width, 'x', textMetrics.height);
      console.log('  - 元素高度与fontSize的比值:', (textMetrics.height / actualFontSizeValue).toFixed(2));

      // 🔍 关键发现：检查是否有额外的padding
      if (actualPaddingTop > 0 || actualPaddingBottom > 0) {
        console.log('⚠️  [发现问题] 字幕元素有padding:', actualPaddingTop, 'px top,', actualPaddingBottom, 'px bottom');
        console.log('  - 这会让字幕看起来比实际fontSize大！');
        console.log('  - 文字本身是', actualFontSize, ', 但元素高度是', textMetrics.height, 'px');
      }
    }, 100);

    // 应用边框（使用 text-shadow，按比例缩放边框宽度）
    const outlineColor = style.outlineColor || '#000000';
    const scaledOutlineWidth = style.outlineWidth * scaleRatio;
    this.subtitleElement.style.textShadow = `
      -${scaledOutlineWidth}px -${scaledOutlineWidth}px 0 ${outlineColor},
      ${scaledOutlineWidth}px -${scaledOutlineWidth}px 0 ${outlineColor},
      -${scaledOutlineWidth}px ${scaledOutlineWidth}px 0 ${outlineColor},
      ${scaledOutlineWidth}px ${scaledOutlineWidth}px 0 ${outlineColor},
      ${scaledOutlineWidth}px ${scaledOutlineWidth}px ${scaledOutlineWidth}px rgba(0,0,0,0.8)
    `;

    // 应用背景色（如果有）
    if (style.backgroundColor && style.backgroundOpacity !== undefined) {
      const r = parseInt(style.backgroundColor.slice(1, 3), 16);
      const g = parseInt(style.backgroundColor.slice(3, 5), 16);
      const b = parseInt(style.backgroundColor.slice(5, 7), 16);
      this.subtitleElement.style.backgroundColor = `rgba(${r}, ${g}, ${b}, ${style.backgroundOpacity})`;
      // 按比例缩放padding
      const scaledPadding = 4 * scaleRatio;
      const scaledPaddingH = 12 * scaleRatio;
      this.subtitleElement.style.padding = `${scaledPadding}px ${scaledPaddingH}px`;
      this.subtitleElement.style.borderRadius = '4px';
    } else {
      this.subtitleElement.style.backgroundColor = 'transparent';
      this.subtitleElement.style.padding = '0';
    }

    // 应用对齐方式（按比例缩放边距，保留scale(1.5)放大效果）
    const scaledMarginV = (style.marginV || 20) * scaleRatio;
    switch (style.alignment) {
      case 'top':
        this.subtitleElement.style.bottom = 'auto';
        this.subtitleElement.style.top = `${scaledMarginV}px`;
        this.subtitleElement.style.left = '50%';
        this.subtitleElement.style.transform = 'translateX(-50%) scale(1.5)';
        break;
      case 'center':
        this.subtitleElement.style.top = '50%';
        this.subtitleElement.style.bottom = 'auto';
        this.subtitleElement.style.left = '50%';
        this.subtitleElement.style.transform = 'translate(-50%, -50%) scale(1.5)';
        break;
      case 'bottom':
      default:
        this.subtitleElement.style.top = 'auto';
        this.subtitleElement.style.bottom = `${scaledMarginV}px`;
        this.subtitleElement.style.left = '50%';
        this.subtitleElement.style.transform = 'translateX(-50%) scale(1.5)';
        break;
    }
  }

  /**
   * 停止当前媒体播放
   */
  private stopCurrentMedia(): void {
    // 清除图片定时器
    if (this.imageTimeoutId) {
      clearTimeout(this.imageTimeoutId);
      this.imageTimeoutId = null;
    }

    // 清除进度更新interval
    if (this.progressInterval) {
      clearInterval(this.progressInterval);
      this.progressInterval = null;
    }

    // 清除音频字幕延迟加载定时器
    if (this.audioSubtitleTimeoutId) {
      clearTimeout(this.audioSubtitleTimeoutId);
      this.audioSubtitleTimeoutId = null;
    }

    this.videoElement.pause();
    this.videoElement.currentTime = 0;

    if (this.audioElement) {
      this.audioElement.pause();
      this.audioElement.currentTime = 0;
    }

    // 停止背景音乐
    if (this.bgMusicElement) {
      this.bgMusicElement.pause();
      this.bgMusicElement.currentTime = 0;
    }
  }

  /**
   * 加载指定场景
   */
  private loadScene(sceneIndex: number): void {
    console.log('🎬 [VideoPlayer] 加载场景', { sceneIndex, totalScenes: this.sceneList.length });

    if (sceneIndex >= this.sceneList.length) {
      console.log('🎬 [VideoPlayer] 所有场景播放完成，显示结束消息', {
        sceneIndex,
        totalScenes: this.sceneList.length,
        isPlaying: this.isPlaying
      });
      this.showEndMessage();
      return;
    }

    this.currentIndex = sceneIndex;
    const scene = this.sceneList[sceneIndex];
    console.log('🎬 [VideoPlayer] 场景数据', {
      id: scene.id,
      hasVideo: !!(scene.videoUrl && scene.videoUrl !== ''),
      hasImages: !!(scene.images && scene.images.length > 0),
      hasAudio: !!(scene.audioUrl && scene.audioUrl !== ''),
      videoUrl: scene.videoUrl,
      firstImageUrl: scene.images?.[0],
      audioUrl: scene.audioUrl,
      cursorPosition: scene.cursorPosition
    });

    const { videoUrl, images, dialogue } = scene;

    // 确保有cursorPosition，如果没有则使用默认值
    // cursorPosition = cursorPosition || { startTime: 0, endTime: 5 }; // 暂时不需要使用，注释掉避免未使用变量警告

    // 初始化视频元素状态
    this.videoElement.srcObject = null;
    this.videoElement.src = '';
    this.videoElement.poster = '';
    this.videoElement.controls = false; // 始终禁用默认控制条

    // 清除之前的图片定时器
    if (this.imageTimeoutId) {
      clearTimeout(this.imageTimeoutId);
      this.imageTimeoutId = null;
    }

    // 重置所有元素的显示状态 - 确保完全重置
    //     console.log('🎬 [VideoPlayer] 重置所有元素显示状态');
    this.videoElement.style.display = 'none';
    this.videoElement.src = '';
    this.videoElement.removeAttribute('src'); // 彻底移除src属性
    this.imageElement.style.display = 'none';
    this.imageElement.src = '';
    this.placeholderElement.style.display = 'none'; // 隐藏占位符

    //     console.log('🎬 [VideoPlayer] 场景类型判断', { videoUrl, hasVideo: !!(videoUrl && videoUrl !== '') });

    if (videoUrl && videoUrl !== '') {
      // 显示视频，隐藏图片
      //       console.log('🎬 [VideoPlayer] 设置显示视频');
      this.videoElement.style.display = 'block';
      this.imageElement.style.display = 'none';
      this.videoElement.src = videoUrl;
      console.log('🎬 加载视频:', videoUrl, {
        videoDisplay: this.videoElement.style.display,
        imageDisplay: this.imageElement.style.display,
        videoSrc: this.videoElement.src
      });

      // ✅ 新增：视频元数据加载完成后重新应用字幕样式（使用实际视频宽度）
      const onVideoLoadedMetadata = () => {
        console.log('🎬 [VideoPlayer] 视频元数据加载完成，重新应用字幕样式', {
          videoWidth: this.videoElement.videoWidth,
          videoHeight: this.videoElement.videoHeight
        });
        // 重新应用字幕样式，使用新的视频分辨率
        this.applySubtitleStyle();
      };

      // 移除旧的监听器（避免重复）
      this.videoElement.removeEventListener('loadedmetadata', onVideoLoadedMetadata);
      this.videoElement.addEventListener('loadedmetadata', onVideoLoadedMetadata, { once: true });

      // 确保视频元数据加载完成
      this.videoElement.load();

      // 设置总时长显示（所有场景的总时长）
      this.totalTimeDisplay.textContent = this.formatTime(this.totalDuration);
    } else {
      // 显示图片，隐藏视频
      //       console.log('🎬 [VideoPlayer] 设置显示图片');
      this.videoElement.style.display = 'none';
      this.placeholderElement.style.display = 'none';

      // 如果没有有效的图片URL，隐藏图片元素
      if (!images[0] || images[0] === '') {
        this.imageElement.style.display = 'none';
      } else {
        // 有有效图片URL，显示图片
        this.imageElement.style.display = 'block';
        const imageUrl = images[0];
        console.log('🎬 加载图片:', imageUrl);
        this.imageElement.src = imageUrl;

        // ✅ 新增：图片加载完成后重新应用字幕样式（使用实际图片宽度）
        const onImageLoad = () => {
          console.log('🎬 [VideoPlayer] 图片加载完成，重新应用字幕样式', {
            imageWidth: this.imageElement.naturalWidth,
            imageHeight: this.imageElement.naturalHeight
          });
          // 重新应用字幕样式，使用新的图片分辨率
          this.applySubtitleStyle();
        };

        // 移除旧的监听器（避免重复）
        this.imageElement.removeEventListener('load', onImageLoad);
        this.imageElement.addEventListener('load', onImageLoad, { once: true });
      }

      // 图片场景的进度更新现在在playScene方法中处理
      if (this.isPlaying) {
        //         console.log('🎬 [VideoPlayer] 图片场景已开始播放，进度更新由playScene方法处理');
      } else {
        //         console.log('🎬 [VideoPlayer] 图片场景加载完成，等待播放命令');
      }
    }

    // 保存字幕内容，但不立即显示（等待startTime）
    this.currentSubtitle = dialogue;
    this.subtitleElement.textContent = ''; // 先清空字幕
    //     console.log('🎬 [VideoPlayer] 字幕内容已保存，将在startTime时显示:', dialogue);

    // 初始化时间显示
    this.currentTimeDisplay.textContent = this.formatTime(0);
    this.totalTimeDisplay.textContent = this.formatTime(this.totalDuration);

    // 调试字幕元素 - 注释掉未使用的变量
    // setTimeout(() => {
    //   const computedStyle = window.getComputedStyle(this.subtitleElement);
    //   const rect = this.subtitleElement.getBoundingClientRect();
    //   //       console.log('🎬 [VideoPlayer] 字幕元素状态检查', {
    //   //         textContent: this.subtitleElement.textContent,
    //   //         innerHTML: this.subtitleElement.innerHTML,
    //   //         display: computedStyle.display,
    //   //         visibility: computedStyle.visibility,
    //   //         opacity: computedStyle.opacity,
    //   //         zIndex: computedStyle.zIndex,
    //   //         position: computedStyle.position,
    //   //         bottom: computedStyle.bottom,
    //   //         left: computedStyle.left,
    //   //         transform: computedStyle.transform,
    //   //         width: computedStyle.width,
    //   //         height: computedStyle.height,
    //   //         backgroundColor: computedStyle.backgroundColor,
    //   //         parent: this.subtitleElement.parentElement?.tagName,
    //   //         rect: {
    //   //           x: rect.x,
    //   //           y: rect.y,
    //   //           width: rect.width,
    //   //           height: rect.height,
    //   //           top: rect.top,
    //   //           left: rect.left,
    //   //           bottom: rect.bottom,
    //   //           right: rect.right
    //   //         },
    //   //         isVisible: rect.width > 0 && rect.height > 0 && computedStyle.display !== 'none'
    //   //       });
    // }, 300);

    // 调试信息：检查video元素的显示状态
    setTimeout(() => {
      //       console.log('🎬 [VideoPlayer] 视频元素状态检查', {
      //         poster: this.videoElement.poster,
      //         src: this.videoElement.src,
      //         style: {
      //           width: this.videoElement.style.width,
      //           height: this.videoElement.style.height,
      //           display: window.getComputedStyle(this.videoElement).display,
      //           visibility: window.getComputedStyle(this.videoElement).visibility,
      //           opacity: window.getComputedStyle(this.videoElement).opacity
      //         },
      //         computedStyle: {
      //           width: window.getComputedStyle(this.videoElement).width,
      //           height: window.getComputedStyle(this.videoElement).height,
      //           objectFit: window.getComputedStyle(this.videoElement).objectFit
      //         }
      //       });
    }, 100);

    // 音频将在playScene方法中处理

    // 设置视频开始时间为0（视频从场景一开始就播放）
    this.videoElement.currentTime = 0;
  }

  /**
   * 播放下一个场景
   */
  public nextScene(): void {
    const nextIndex = this.currentIndex + 1;
    this.loadScene(nextIndex);

    // 只有当不是最后一个场景时才自动播放
    if (nextIndex < this.sceneList.length) {
      this.play();
    }
  }

  /**
   * 从指定场景开始播放
   */
  public playFromScene(sceneIndex: number): void {
    //     console.log('🎬 [VideoPlayer] playFromScene() 方法被调用', {
    //       targetSceneIndex: sceneIndex,
    //       currentSceneIndex: this.currentIndex,
    //       totalScenes: this.sceneList.length
    //     });

    // 验证场景索引
    if (!this.validateSceneIndex(sceneIndex)) {
      //       console.error('🎬 [VideoPlayer] 无效的场景索引:', sceneIndex);
      return;
    }

    // 停止当前播放
    this.stopCurrentMedia();

    // 重置播放状态
    this.hasEnded = false;
    this.currentIndex = sceneIndex;

    //     console.log('🎬 [VideoPlayer] 开始加载目标场景', {
    //       sceneIndex,
    //       sceneId: this.sceneList[sceneIndex]?.id
    //     });

    // 加载目标场景
    this.loadScene(sceneIndex);

    // 开始播放
    this.play();
  }

  /**
   * 加载指定场景但不自动播放
   */
  public loadSceneAtIndex(sceneIndex: number): void {
    //     console.log('🎬 [VideoPlayer] loadSceneAtIndex() 方法被调用', {
    //       targetSceneIndex: sceneIndex,
    //       currentSceneIndex: this.currentIndex,
    //       totalScenes: this.sceneList.length
    //     });

    // 验证场景索引
    if (!this.validateSceneIndex(sceneIndex)) {
      //       console.error('🎬 [VideoPlayer] 无效的场景索引:', sceneIndex);
      return;
    }

    // 停止当前播放
    this.stopCurrentMedia();

    // 重置播放状态
    this.hasEnded = false;
    this.currentIndex = sceneIndex;

    //     console.log('🎬 [VideoPlayer] 开始加载目标场景', {
    //       sceneIndex,
    //       sceneId: this.sceneList[sceneIndex]?.id
    //     });

    // 加载目标场景
    this.loadScene(sceneIndex);
  }

  /**
   * 验证场景索引是否有效
   */
  private validateSceneIndex(sceneIndex: number): boolean {
    const isValid = (
      typeof sceneIndex === 'number' &&
      sceneIndex >= 0 &&
      sceneIndex < this.sceneList.length
    );

    //     console.log('🎬 [VideoPlayer] 场景索引验证', {
    //       sceneIndex,
    //       isValid,
    //       totalScenes: this.sceneList.length
    //     });

    return isValid;
  }

  /**
   * 重新加载场景数据并从指定场景开始播放
   * @param newSceneList 新的场景列表
   * @param startSceneIndex 开始播放的场景索引，默认为0
   * @returns 新的播放器实例
   */
  public reloadAndPlayFromScene(newSceneList: any[], startSceneIndex: number = 0): PureFrontendVideoPlayer {
    //     console.log('🎬 [VideoPlayer] reloadAndPlayFromScene() 方法被调用，已委托给 reloadToScene(autoPlay=true)');
    // 该方法是 reloadToScene 的简化版本，自动播放参数固定为 true
    return this.reloadToScene(newSceneList, startSceneIndex, true);
  }

  /**
   * 重新加载场景数据到指定场景，可选择是否自动播放
   * @param newSceneList 新的场景列表
   * @param startSceneIndex 目标场景索引，默认为0
   * @param autoPlay 是否自动播放，默认为false
   * @returns 新的播放器实例
   */
  public reloadToScene(newSceneList: any[], startSceneIndex: number = 0, autoPlay: boolean = false): PureFrontendVideoPlayer {
    //     console.log('🎬 [VideoPlayer] reloadToScene() 方法被调用', {
    //       newSceneListLength: newSceneList.length,
    //       startSceneIndex,
    //       autoPlay,
    //       currentSceneIndex: this.currentIndex
    //     });

    // 验证新场景数据
    if (!Array.isArray(newSceneList) || newSceneList.length === 0) {
      //       console.error('🎬 [VideoPlayer] 无效的场景列表数据:', newSceneList);
      throw new Error('无效的场景列表数据');
    }

    // 验证开始场景索引
    if (startSceneIndex < 0 || startSceneIndex >= newSceneList.length) {
      //       console.error('🎬 [VideoPlayer] 无效的开始场景索引:', startSceneIndex);
      throw new Error('无效的开始场景索引');
    }

    // 记住当前容器
    const container = this.container;

    //     console.log('🎬 [VideoPlayer] 准备销毁当前实例并创建新实例', {
    //       currentSceneId: this.sceneList[this.currentIndex]?.id,
    //       targetSceneId: newSceneList[startSceneIndex]?.id,
    //       autoPlay
    //     });

    // 停止媒体但不销毁DOM元素
    this.stopCurrentMedia();

    // 清空容器内容（为新的播放器腾出空间）
    container.innerHTML = '';

    // 创建新的播放器实例（跳过自动加载），并传递当前播放器的背景音乐参数和字幕样式
    const newInstance = new PureFrontendVideoPlayer(
      container,
      newSceneList,
      true,
      this.bgMusicUrl,
      this.bgMusicVolume,
      undefined,
      undefined,
      this.subtitleStyle  // ← 传递字幕样式
    );

    // 从指定场景开始加载或播放
    if (autoPlay) {
      // 如果自动播放，使用playFromScene
      //       console.log('🎬 [VideoPlayer] reloadToScene(autoPlay=true)，调用 playFromScene，场景索引:', startSceneIndex);
      newInstance.playFromScene(startSceneIndex);
      //       console.log('🎬 [VideoPlayer] 新播放器实例创建完成，已开始自动播放');
    } else {
      // 如果不自动播放，仅加载场景
      //       console.log('🎬 [VideoPlayer] reloadToScene(autoPlay=false)，调用 loadSceneAtIndex，场景索引:', startSceneIndex);
      newInstance.loadSceneAtIndex(startSceneIndex);
      //       console.log('🎬 [VideoPlayer] 新播放器实例创建完成，场景已加载，等待手动播放');
    }

    return newInstance;
  }


  /**
   * 开始播放
   */
  public play(): void {
    console.log('🎬 [VideoPlayer] play() 方法被调用', {
      currentIndex: this.currentIndex,
      sceneListLength: this.sceneList.length,
      isPlaying: this.isPlaying,
      hasEnded: this.hasEnded
    });

    // 如果播放已结束，重置播放状态
    if (this.hasEnded) {
      console.log('🎬 [VideoPlayer] 播放已结束，重置状态');
      this.hasEnded = false;
      this.isPlaying = false;

      // 启用播放按钮
      this.playPauseButton.disabled = false;
      this.playPauseButton.style.opacity = '1';
      this.playPauseButton.style.cursor = 'pointer';

      // 重置字幕
      this.subtitleElement.textContent = '';

      // 重置进度条
      this.progressBar.value = '0';
      this.currentTimeDisplay.textContent = this.formatTime(0);

      // 加载第一个场景
      this.loadScene(0);

      // 重置后继续执行播放逻辑，不需要用户再次点击
    }

    const currentScene = this.sceneList[this.currentIndex];
    if (!currentScene) {
      console.error('🎬 [VideoPlayer] 当前场景不存在');
      return;
    }

    console.log('🎬 [VideoPlayer] 开始播放场景', this.currentIndex, currentScene);

    // 如果有暂停记录，从暂停的场景重新开始
    if (this.pausedSceneIndex >= 0) {
      console.log('🎬 [VideoPlayer] 从暂停场景恢复播放', {
        pausedSceneIndex: this.pausedSceneIndex,
        currentSceneIndex: this.currentIndex
      });

      const targetSceneIndex = this.pausedSceneIndex;
      this.pausedSceneIndex = -1; // 重置暂停记录

      // 加载目标场景并开始播放
      this.loadScene(targetSceneIndex);
      this.play(); // 递归调用，但此时pausedSceneIndex已重置，会正常执行
      return;
    }

    // 如果是图片场景，启动定时器和进度更新
    if (!currentScene.videoUrl || currentScene.videoUrl === '') {
      console.log('🎬 [VideoPlayer] 图片场景，开始播放图片场景');
      const { startTime, endTime } = currentScene.cursorPosition || { startTime: 0, endTime: 5 };
      // 图片场景持续时间是endTime（图片从场景一开始就显示，直到endTime结束）
      const durationSeconds = endTime;
      const durationMs = durationSeconds * 1000; // 转换为毫秒

      console.log('🎬 [VideoPlayer] 图片场景设置:', {
        startTime,
        endTime,
        durationSeconds,
        durationMs,
        hasImages: !!(currentScene.images && currentScene.images.length > 0),
        imageUrl: currentScene.images?.[0]
      });

      // 清除之前的定时器
      if (this.imageTimeoutId) {
        clearTimeout(this.imageTimeoutId);
        this.imageTimeoutId = null;
      }

      // 清除之前的进度更新器
      if (this.progressInterval) {
        clearInterval(this.progressInterval);
        this.progressInterval = null;
      }

      // 创建进度更新定时器
      const sceneStartTime = Date.now();

      const progressInterval = setInterval(() => {
        const elapsedInScene = (Date.now() - sceneStartTime) / 1000; // 当前场景内的时间
        const elapsedUpToCurrent = this.getElapsedTimeUpToCurrentScene();
        const elapsedTotal = elapsedUpToCurrent + Math.min(elapsedInScene, durationSeconds); // 总累计时间
        const progress = this.totalDuration > 0 ? (elapsedTotal / this.totalDuration) * 100 : 0;

        // 更新进度条
        this.progressBar.value = progress.toString();
        this.currentTimeDisplay.textContent = this.formatTime(elapsedTotal);
        this.totalTimeDisplay.textContent = this.formatTime(this.totalDuration);

        // 同步背景音乐时间
        this.syncBgMusicTime(elapsedTotal);
      }, 100); // 每100ms更新一次进度

      // 保存进度更新interval
      this.progressInterval = progressInterval as unknown as number;

      this.imageTimeoutId = window.setTimeout(() => {
        console.log('🎬 [VideoPlayer] 图片定时器触发，切换到下一场景');
        // 清除进度更新定时器
        if (this.progressInterval) {
          clearInterval(this.progressInterval);
          this.progressInterval = null;
        }
        this.stopCurrentMedia();
        this.nextScene();
      }, durationMs);
    } else {
      console.log('🎬 [VideoPlayer] 视频场景，开始播放视频场景', {
        videoUrl: currentScene.videoUrl,
        hasVideo: !!(currentScene.videoUrl)
      });

      const { startTime = 0, endTime } = currentScene.cursorPosition || { startTime: 0, endTime: 5 };
      // 视频播放持续时间是endTime（视频从场景一开始就播放，直到endTime结束）
      const videoDuration = endTime;

      console.log('🎬 [VideoPlayer] 视频播放参数:', {
        startTime,
        endTime,
        videoDuration,
        videoElementReady: this.videoElement.readyState,
        videoSrc: this.videoElement.src
      });

      // 等待视频可播放后再播放
      const attemptPlay = () => {
        console.log('🎬 [VideoPlayer] 尝试播放视频，准备状态:', this.videoElement.readyState);
        if (this.videoElement.readyState >= 3) { // HAVE_FUTURE_DATA
          console.log('🎬 [VideoPlayer] 视频已准备好，开始播放');
          this.videoElement.play().then(() => {
            console.log('🎬 [VideoPlayer] 视频播放成功');

            // 设置视频在endTime时强制停止
            setTimeout(() => {
              if (this.videoElement && !this.videoElement.paused) {
                console.log('🎬 [VideoPlayer] 到达视频场景endTime，强制停止视频');
                this.videoElement.pause();
              }
            }, videoDuration * 1000);

          }).catch(err => {
            console.error('🎬 [VideoPlayer] 视频播放失败:', err);
          });
        } else {
          console.log('🎬 [VideoPlayer] 视频还未准备好，等待中...', {
            readyState: this.videoElement.readyState,
            networkState: this.videoElement.networkState
          });
          setTimeout(attemptPlay, 500);
        }
      };

      attemptPlay();
    }

    // 延迟加载音频和字幕，等待startTime时刻
    this.scheduleAudioAndSubtitle(currentScene);

    // 播放背景音乐
    if (this.bgMusicElement && !this.bgMusicElement.paused) {
      // 已经在播放，无需操作
    } else if (this.bgMusicElement) {
      this.bgMusicElement.currentTime = 0; // 重置到开始位置
      this.bgMusicElement.play().catch(err => {
        console.error('🎬 [VideoPlayer] 背景音乐播放失败:', err);
      });
    }

    this.isPlaying = true;
    this.updatePlayPauseButton(true);
    console.log('🎬 [VideoPlayer] play() 方法执行完成，isPlaying:', this.isPlaying);
  }

  /**
   * 暂停播放
   */
  public pause(): void {
    //     console.log('🎬 [VideoPlayer] pause() 方法被调用', {
    //       currentIndex: this.currentIndex,
    //       isPlaying: this.isPlaying,
    //       hasImageTimeout: !!this.imageTimeoutId,
    //       hasProgressInterval: !!this.progressInterval
    //     });

    // 记录暂停时的场景索引
    this.pausedSceneIndex = this.currentIndex;

    this.videoElement.pause();
    this.audioElement?.pause();

    // 暂停背景音乐
    if (this.bgMusicElement && !this.bgMusicElement.paused) {
      this.bgMusicElement.pause();
    }

    // 停止图片场景的定时器和进度更新
    if (this.imageTimeoutId) {
      //       console.log('🎬 [VideoPlayer] 停止图片场景定时器');
      clearTimeout(this.imageTimeoutId);
      this.imageTimeoutId = null;
    }

    if (this.progressInterval) {
      //       console.log('🎬 [VideoPlayer] 停止进度更新定时器');
      clearInterval(this.progressInterval);
      this.progressInterval = null;
    }

    // 停止音频字幕的延迟加载
    if (this.audioSubtitleTimeoutId) {
      //       console.log('🎬 [VideoPlayer] 停止音频字幕延迟加载');
      clearTimeout(this.audioSubtitleTimeoutId);
      this.audioSubtitleTimeoutId = null;
    }

    this.isPlaying = false;
    this.updatePlayPauseButton(false);

    //     console.log('🎬 [VideoPlayer] pause() 方法执行完成', {
    //       isPlaying: this.isPlaying,
    //       pausedSceneIndex: this.pausedSceneIndex
    //     });
  }

  /**
   * 显示播放结束消息
   */
  private showEndMessage(): void {
    //     console.log('🎬 [VideoPlayer] 执行结束逻辑', {
    //       isPlaying: this.isPlaying,
    //       currentIndex: this.currentIndex,
    //       sceneCount: this.sceneList.length
    //     });

    // 设置播放结束标志
    this.hasEnded = true;

    // 停止所有媒体
    this.stopCurrentMedia();

    // 清除所有定时器
    if (this.imageTimeoutId) {
      clearTimeout(this.imageTimeoutId);
      this.imageTimeoutId = null;
    }
    if (this.progressInterval) {
      clearInterval(this.progressInterval);
      this.progressInterval = null;
    }

    // 隐藏视频和图片
    this.videoElement.style.display = 'none';
    this.imageElement.style.display = 'none';

    // 重置播放状态
    this.videoElement.poster = '';
    this.subtitleElement.textContent = i18n.t('videoPlayer.allScenesCompleted');
    this.isPlaying = false;
    this.updatePlayPauseButton(false);

    // 将进度设置为100%
    this.progressBar.value = '100';
    this.currentTimeDisplay.textContent = this.formatTime(this.totalDuration);

    // 不要禁用播放按钮，允许用户点击重新播放
    this.playPauseButton.disabled = false;
    this.playPauseButton.style.opacity = '1';
    this.playPauseButton.style.cursor = 'pointer';

    //     console.log('🎬 [VideoPlayer] 结束逻辑执行完成', {
    //       isPlaying: this.isPlaying,
    //       progressBar: this.progressBar.value,
    //       hasEnded: this.hasEnded
    //     });
  }

  /**
   * 切换播放/暂停
   */
  private togglePlayPause(): void {
    if (this.isPlaying) {
      this.pause();
    } else {
      this.play();
    }
  }

  /**
   * 更新播放/暂停按钮状态
   */
  private updatePlayPauseButton(isPlaying: boolean): void {
    this.isPlaying = isPlaying;
    this.playPauseButton.innerHTML = isPlaying ? '❚❚' : '▶';
  }

  /**
   * 处理进度条点击 - 跳转到对应场景
   */
  private handleProgressBarClick(event: MouseEvent): void {
    //     console.log('🎬 [VideoPlayer] 进度条点击事件触发');

    const progressBar = this.progressBar;
    const rect = progressBar.getBoundingClientRect();
    const clickX = event.clientX - rect.left;
    const progressBarWidth = rect.width;

    if (progressBarWidth <= 0) return;

    const clickPercentage = clickX / progressBarWidth;
    const targetSceneIndex = this.calculateSceneIndexByPercentage(clickPercentage);

    //     console.log('🎬 [VideoPlayer] 进度条点击跳转', {
    //       clickX,
    //       progressBarWidth,
    //       clickPercentage,
    //       targetSceneIndex,
    //       totalScenes: this.sceneList.length
    //     });

    // 跳转到目标场景
    this.playFromScene(targetSceneIndex);
  }

  /**
   * 根据百分比计算场景索引
   */
  private calculateSceneIndexByPercentage(percentage: number): number {
    if (this.totalDuration <= 0) return 0;

    const targetTime = this.totalDuration * percentage;
    let accumulatedTime = 0;

    for (let i = 0; i < this.sceneList.length; i++) {
      const sceneEndTime = this.sceneList[i].cursorPosition?.endTime || 0;
      accumulatedTime += sceneEndTime;

      if (accumulatedTime >= targetTime) {
        //         console.log('🎬 [VideoPlayer] 计算目标场景', {
        //           percentage,
        //           targetTime,
        //           sceneIndex: i,
        //           accumulatedTime,
        //           sceneId: this.sceneList[i]?.id
        //         });
        return i;
      }
    }

    return this.sceneList.length - 1;
  }

  /**
   * 处理进度条变化
   */
  private handleProgressChange(): void {
    const progress = parseFloat(this.progressBar.value);
    const duration = this.videoElement.duration || 100;
    const newTime = (progress / 100) * duration;
    this.videoElement.currentTime = newTime;

    if (this.audioElement) {
      this.audioElement.currentTime = newTime;
    }
  }

  /**
   * 更新进度条 - 暂时未使用
   */
  // private updateProgressBar(): void {
  //   // 这个方法可能被直接调用，需要使用正确的累计时间逻辑
  //   if (this.sceneList.length === 0) return;

  //   const currentScene = this.sceneList[this.currentIndex];
  //   if (!currentScene) return;

  //   const { startTime = 0 } = currentScene.cursorPosition;

  //   // 计算累计播放时间
  //   const elapsedInScene = this.videoElement.currentTime - startTime;
  //   const elapsedTotal = this.getElapsedTimeUpToCurrentScene() + Math.max(0, elapsedInScene);
  //   const progress = this.totalDuration > 0 ? (elapsedTotal / this.totalDuration) * 100 : 0;

  //   this.progressBar.value = progress.toString();
  //   this.currentTimeDisplay.textContent = this.formatTime(elapsedTotal);
  //   this.totalTimeDisplay.textContent = this.formatTime(this.totalDuration);
  // }

  /**
   * 处理元数据加载完成
   */
  private handleLoadedMetadata(): void {
    // 使用总时长而不是视频时长
    this.totalTimeDisplay.textContent = this.formatTime(this.totalDuration);
  }

  /**
   * 格式化时间显示
   */
  private formatTime(seconds: number): string {
    // 确保时间不为负数
    const positiveSeconds = Math.max(0, seconds);
    const mins = Math.floor(positiveSeconds / 60);
    const secs = Math.floor(positiveSeconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }

  /**
   * 添加自定义样式
   */
  private addCustomStyles(): void {
    const style = document.createElement('style');
    style.textContent = `
      .slider::-webkit-slider-thumb {
        appearance: none;
        width: 12px;
        height: 12px;
        background: white;
        border-radius: 50%;
        cursor: pointer;
      }
      .slider::-moz-range-thumb {
        width: 12px;
        height: 12px;
        background: white;
        border-radius: 50%;
        cursor: pointer;
        border: none;
      }
    `;
    document.head.appendChild(style);
  }

  /**
   * 销毁实例
   */
  public destroy(): void {
    // 停止媒体（这会清理所有定时器）
    this.stopCurrentMedia();

    // 清理背景音乐元素
    if (this.bgMusicElement) {
      this.bgMusicElement.pause();
      this.bgMusicElement.src = '';
      this.bgMusicElement = null;
    }

    // 移除DOM元素
    this.videoElement.remove();
    this.imageElement.remove();
    this.subtitleElement.remove();
    this.audioElement?.remove();
    this.controlsContainer.remove();
    this.mediaContainer.remove();
  }
}
